import React from "react";
import { Box, Typography, keyframes } from "@mui/material";
import { useTheme } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import { LoadingButton } from "../blocks/LoadingButton.js";
import cityscapeSvg from "../../assets/svg/city-scape3.svg";

// Subtle floating animation for overlay elements
const gentleFloat = keyframes`
  0%, 100% { 
    transform: translateY(0px);
  }
  50% { 
    transform: translateY(-8px);
  }
`;

// Seamless horizontal scrolling animation for cityscape
// With 4 copies, we animate to -25% so that when the first image
// moves completely out of view, the animation restarts seamlessly
const cityScroll = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-25%);
  }
`;

const CreatorFundExplainer = ({ variant = "light", sx, ...props }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { primary, secondary, text, overlay } = theme.palette;
  const isDark = variant === "dark";

  const handleButtonClick = () => {
    navigate("/vision");
  };

  // Dark mode styles
  const containerStyles = isDark
    ? {
        backgroundColor: primary.main,
        background: primary.main,
        border: `1px solid ${overlay.white30}`,
      }
    : {
        backgroundColor: theme.palette.background.paper,
      };

  const textColor = isDark ? primary.contrastText : text.primary;
  const secondaryTextColor = isDark ? overlay.white70 : text.secondary;
  const iconColor = isDark ? primary.contrastText : text.secondary;

  return (
    <Box
      sx={{
        textAlign: "center",
        py: 5,
        px: 4,
        borderRadius: 8,
        maxWidth: "calc(400px - 66px)",
        ...containerStyles,
        ...sx,
      }}
      {...props}
    >
      <Box
        sx={{
          width: "100%",
          textAlign: "center",
          maxWidth: "300px",
          margin: "0 auto",
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            fontSize: "3rem",
            color: iconColor,
            fontFamily: "Material Symbols Outlined",
            fontWeight: 100,
            display: "block",
            margin: "0 auto",
            mb: 1,
          }}
        >
          volunteer_activism
        </Box>

        {/* Title */}
        <Typography
          variant="title"
          sx={{
            color: textColor,
            lineHeight: 1.2,
            fontWeight: 300,
            display: "block",
          }}
        >
          Your Membership
        </Typography>
        <Typography 
            variant="title"
            sx={{
              color: textColor,
              lineHeight: 1.2,
              fontWeight: 600,
              display: "block",
              mb: 1,
            }}
        >
          Builds Communities.
        </Typography>

        {/* Animation Area */}
        <Box
          sx={{
            position: "relative",
            mt: -.25, 
            width: "100%",
            height: "170px",
            borderRadius: isDark ? "12px" : 0,
            backgroundColor: isDark ? primary.main : "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            transition: "all 0.6s ease-in-out",
            ...(isDark && {
              boxShadow: theme.shadows.cardSm(theme.palette, "dark"),
              mx: -4, // Extend beyond container padding in dark mode
              width: "calc(100% + 64px)", // Account for px: 4 on both sides (4 * 8px * 2 = 64px)
            }),
          }}
        >
          {/* Animated cityscape background container */}
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              height: "100%",
              overflow: "hidden",
            }}
          >
            {/* Animated cityscape strip - multiple copies for seamless loop */}
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                height: "100%",
                display: "flex",
                animation: `${cityScroll} 30s linear infinite`,
                willChange: "transform",
                filter: isDark ? "invert(1) brightness(0.9)" : "none",
              }}
            >
              {/* Multiple cityscape copies to ensure no gaps */}
              {[...Array(4)].map((_, index) => (
                <img
                  key={index}
                  src={cityscapeSvg}
                  alt="cityscape"
                  style={{
                    height: "100%",
                    width: "auto",
                    display: "block",
                    objectFit: "contain",
                    objectPosition: "bottom",
                    verticalAlign: "bottom",
                    marginLeft: index === 0 ? 0 : "-1px", // Slight overlap to prevent gaps
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>

        {/* Content Below Animation */}
        <Typography
          variant="body1"
          sx={{
            mt: 1,
            mb: 2,
            color: secondaryTextColor,
            maxWidth: "280px",
            mx: "auto",
          }}
        >
          Learn about our mission to give back through our Creator Fund.
        </Typography>

        {/* CTA Button */}
        <LoadingButton
          variant="contained"
          size="shrinked"
          onClick={handleButtonClick}
          sx={{
            mb: 2,
            fontWeight: 600,
            textTransform: "none",
            boxShadow: theme.shadows.cardSm(
              theme.palette,
              isDark ? "dark" : "light"
            ),
            ...(isDark && {
              backgroundColor: secondary.main,
              color: secondary.contrastText,
              "&:hover": {
                backgroundColor: overlay.white90,
              },
            }),
          }}
        >
          SEE THE IMPACT
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default CreatorFundExplainer; 